import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import ErrorMessage from '../../components/errormessage';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import { enforceSlash } from '../../utils/helpers';
import { useStores } from '../../hooks/use-stores';
import { observer } from 'mobx-react';
import Grid from '@material-ui/core/Grid';
import { Typography, TextField } from '@material-ui/core';
import { Box } from '@material-ui/core';
import DoneIcon from '@material-ui/icons/Done';
import ClearIcon from '@material-ui/icons/Clear';
import SimpleTable from '../../components/super/SimpleTable';
import { runInAction } from 'mobx';
import NumberInput from '../../components/input/NumberInput';
import Button from '@material-ui/core/Button';

function Categories() {
    const { superStore } = useStores();
    const { pathname } = useLocation();
    const baseUrl = enforceSlash(pathname);
    const {
        loadingCategories,
        categories,
        categoriesError,
        sortedCategories,
        creatingCategory,
    } = superStore;
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryOrder, setNewCategoryOrder] = useState(0);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        superStore.loadCategories();
    };

    const reorderCategories = () => {
        runInAction(() => {
            superStore.reorderingCategories = true;
            superStore.categories = superStore.sortedCategories
        });

        setTimeout(() => {
            runInAction(() => {
                superStore.reorderingCategories = false;
            });
        }, 0);
    }

    const updateOrder = async (categoryId, orderNumber) => {
        superStore.updateCategoryOrder(categoryId, orderNumber);
        await superStore.debouncedSaveCategoryOrder(
            categoryId,
            orderNumber
        )
    };

    const createCategory = async () => {
        if (!newCategoryName.trim()) return;
        await superStore.createCategory({
            name: newCategoryName.trim(),
            order: newCategoryOrder,
        });
        setNewCategoryName('');
        setNewCategoryOrder(0);
    };

    const columns = React.useMemo(
        () => [
            {
                Header: 'Native Name',
                Cell: (x) => (
                    <Link to={`${baseUrl}${x.row.original.id}`}>
                        {x.value}
                    </Link>
                ),
                accessor: 'name',
            },
            {
                Header: '# of Rules',
                accessor: 'rulesCount',
            },
            {
                Header: 'Public',
                accessor: 'isPublic',
                Cell: (x) =>
                    x.value > 0 ? (
                        <DoneIcon color="primary" />
                    ) : (
                        <ClearIcon color="secondary" />
                    ),
            },
            {
                Header: '# of Subjects',
                accessor: 'subjects',
            },
            {
                Header: 'Id',
                accessor: 'id',
                Cell: (x) => (
                    <Link to={`${baseUrl}${x.value}`}>
                        {x.value}
                    </Link>
                ),
            },
            {
                Header: 'Order',
                accessor: 'order',
                textAlign: 'center',
                Cell: observer((x) => {
                    let value = superStore.categories.find(
                        (category) => category.id === x.row.original.id
                    ).order;

                    return (
                        <NumberInput
                            value={value}
                            onChange={(orderNumber) =>
                                updateOrder(x.row.original.id, orderNumber)
                            }
                            min={0}
                            step={1}
                        />
                    );
                }),
            },
        ],
        []
    );

    return (
        <Grid
            container
            direction="row"
            justify="center"
            align="center"
            spacing={2}
        >
            <Grid item xs={12}>
                <Box mt={4}>
                    <Typography variant="h2">Categories</Typography>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item xs={6} md={4}>
                        <TextField
                            label="Category name"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={3} md={2}>
                        <NumberInput
                            value={newCategoryOrder}
                            onChange={(value) => setNewCategoryOrder(value)}
                            min={0}
                            step={1}
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={createCategory}
                            disabled={creatingCategory}
                        >
                            {creatingCategory ? 'Creating...' : 'Create Category'}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            {loadingCategories ? (
                <ThemedSpinner />
            ) : categories && categories.length > 0 ? (
                <Grid item xs={12}>
                    <SimpleTable
                        columns={columns}
                        data={sortedCategories}
                        needsFilter={true}
                        extraAction={
                            <Button
                                onClick={reorderCategories}
                                color="primary"
                                variant="contained"
                            >
                                Reorder
                            </Button>
                        }
                    />
                </Grid>
            ) : (
                <Grid item xs={12}>
                    <Typography>No categories found :(</Typography>
                </Grid>
            )}
            {categoriesError && (
                <Grid item>
                    <ErrorMessage error={categoriesError} />
                </Grid>
            )}
        </Grid>
    );
}

export default observer(Categories);
